import AssigneeStack, { type StackPerson } from "@/components/shared/AssigneeStack";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { resolveAvatarUrl } from "@/lib/avatarUtils";
import { getDisplayName, getUserInitials } from "@/lib/userUtils";
import { cn } from "@/lib/utils";

interface AssignmentSummaryProps {
  /** The assignees in the ticket's own order — `[0]` is the primary. */
  people: StackPerson[];
  /**
   * How the helpers read.
   *
   * "stack" — overlapping faces to the right of the primary. For a strip on a
   * page, where the row has to stay one line tall.
   *
   * "list" — one helper per line under the primary, each with a face and a
   * full NAME. For the hover panel, which is opened precisely to find out who
   * these people are: a second stack of faces there just repeats the cell the
   * cursor is already on.
   */
  helperLayout?: "stack" | "list";
  /** Drawn when nobody is assigned. */
  emptyLabel?: string;
  className?: string;
}

/**
 * The assignment, said in words rather than in faces: the primary named in
 * full under a "Primary" label, with the helpers beside or beneath.
 *
 * The stack alone answers "is this mine?" at a glance, which is all a table
 * column has room for. It cannot answer "who owns it" — the star says *that
 * one*, not *who*. So wherever there is room for the sentence, this is the
 * shape it takes: the ticket's own page, and the hover panel over the
 * backlog's stack, drawn from one component so the tip is recognisably a
 * preview of the page it opens.
 */
const AssignmentSummary = ({ people, helperLayout = "stack", emptyLabel = "—", className }: AssignmentSummaryProps) => {
  if (people.length === 0) {
    return <span className={cn("text-muted-foreground", className)}>{emptyLabel}</span>;
  }

  const [primary, ...helpers] = people;

  return (
    <div className={cn("flex min-w-0 flex-col gap-2", className)}>
      <div className="flex min-w-0 items-center gap-2.5">
        {/* The primary on its own, so the star lands on a face with nothing
            overlapping it. `primaryStar="always"`: a one-face stack suppresses
            the star by default (see AssigneeStack), and here the word
            "Primary" is printed directly under it — the glyph and the label
            have to agree. */}
        <AssigneeStack people={[primary]} size="lg" primaryStar="always" />
        <span className="min-w-0 leading-tight">
          <span className="block truncate font-semibold text-foreground">{getDisplayName(primary)}</span>
          <span className="block text-[10.5px] font-medium uppercase tracking-wide text-muted-foreground">Primary</span>
        </span>
        {helperLayout === "stack" && helpers.length > 0 && (
          <span className="ml-auto flex shrink-0 items-center gap-2 pl-2">
            <AssigneeStack people={helpers} size="md" max={4} primaryStar="never" />
          </span>
        )}
      </div>

      {helperLayout === "list" && helpers.length > 0 && (
        // Deliberately the same face size and the same line as the panel's
        // "Added by" row underneath: these are all just people on the ticket,
        // and three different registers in a 200px panel reads as three
        // different KINDS of fact.
        <div className="flex flex-col gap-1.5">
          {helpers.map((person) => (
            <div key={person.id} className="flex items-center gap-2 text-[11.5px]">
              <Avatar className="h-5 w-5 shrink-0">
                {person.avatar_url && <AvatarImage src={resolveAvatarUrl(person.avatar_url)} alt="" />}
                <AvatarFallback className="avatar-plate text-[9px] font-medium text-primary dark:text-blue-200">
                  {getUserInitials(person)}
                </AvatarFallback>
              </Avatar>
              <span className="min-w-0 truncate text-foreground">{getDisplayName(person)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AssignmentSummary;
